<template>
  <div class="custom-offers">
    <div class="custom-offers__head px-3 px-md-4 py-3 d-flex align-items-center">
      <h2 class="mb-0">
        {{ i18n.custom_offers_heading }}
      </h2>

      <button class="custom-offers__head-action ml-auto btn--reset color-white d-none d-md-inline-block" @click="onPrint">
        <icon identifier="print" /> {{ i18n.print }}
      </button>
    </div>

    <div class="p-3 p-md-4">
      <div class="row flex-row-lg-reverse">
        <div class="col-lg-6">
          <h3 class="font-size-lg mb-3" v-if="responseOptions.title">
            {{ responseOptions.title }}
          </h3>

          <div class="mb-3" v-if="responseOptions.dimensions">
            <div>{{ responseOptions.dimensions }}</div>
            <div class="font-size-sm text-muted">{{ responseOptions.dimensionsLabel }}</div>
          </div>

          <ul class="list mb-3" v-if="responseOptions.list">
            <li v-for="item in responseOptions.list" :key="item">
              {{ item }}
            </li>
          </ul>

          <usps :usps="responseOptions.usps" />

          <button class="mt-3 custom-offers__cutting-costs-btn" type="button" @click="openCuttingCostsModal" v-if="cuttingCostsModalContent">
            {{ i18n.open_cutting_costs_info }}
          </button>

          <modal
            :open="cuttingCostsModalOpen"
            :title="i18n.cutting_costs_heading"
            :content="cuttingCostsModalContent"
            @hide="closeCuttingCostsModal"
            v-if="cuttingCostsModalContent"
          />

          <div class="stock-offers-shortcut mt-3 mt-md-4" v-if="responseStockOffers">
            <strong class="stock-offers-shortcut__title d-block mb-1">
              <icon identifier="truck" />
              {{ i18n.stock_offers_shortcut_title }}
            </strong>

            <button type="button" class="btn--reset stock-offers-shortcut__button" @click="onStockOfferShortcutClick">
              {{ i18n.stock_offers_shortcut_link.replace(':num', responseStockOffers.length) }}
              <icon identifier="arrow-down" />
            </button>
          </div>
        </div>

        <div v-if="!responseOptions.offerSuggestion" class="col-lg-6 mt-4 mt-lg-0">
          <h3 class="font-size-lg mb-3 mb-md-4">
            {{ i18n.custom_offers_title }}
          </h3>

          <offers-list
            class="mb-3 mb-md-4"
            :offers="responseCustomOffers"
            @change="onChangeOffer"
          />

          <button
            class="custom-offers__cart btn btn--secondary w-100 btn--lg"
            type="button"
            :disabled="!activeCustomOffer || !activeCustomOffer.amount"
            @click="onAddToCart"
          >
            {{ i18n.add_to_cart }}
          </button>
        </div>

        <div v-else class="col-lg-6 mt-4 mt-lg-0">
          <div class="mb-3 mb-md-4 p-3 bg-mute custom-offers__suggestion">
            {{ responseOptions.offerSuggestion }}
          </div>

          <a
            class="custom-offers__cart btn btn--secondary w-100 btn--lg"
            :href="responseOptions.offerSuggestionUrl"
          >
            {{ i18n.suggest_offer }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from './Icon';
import OffersList from './OffersList';
import Usps from './Usps';
import jump from 'jump.js';
import Modal from "./Modal";

export default {
  components: {
    Modal,
    Usps,
    OffersList,
    Icon,
  },

  data() {
    return {
      offer: null,
      cuttingCostsModalOpen: false,
    };
  },

  computed: {
    ...mapGetters([
      'responseOptions',
      'responseCustomOffers',
      'responseStockOffers',
      'formInput',
      'activeCustomOffer',
      'activeBoxSpecification',
    ]),

    cuttingCostsModalContent() {
      if (!this.activeBoxSpecification) {
        return null;
      }

      return this.activeBoxSpecification.cutting_costs_explanation;
    },
  },

  methods: {
    onChangeOffer(offer) {
      this.$store.commit('setActiveCustomOffer', offer);
    },

    onAddToCart() {
      const { formInput } = this;

      formInput.amount = this.activeCustomOffer.amount;
      formInput.price = this.activeCustomOffer.price;
      formInput.pricePerPiece = this.activeCustomOffer.price_per_piece;

      this.gtmAddToCart(formInput);

      this.$store.dispatch('addCustomOfferToCart', formInput);
    },

    onStockOfferShortcutClick() {
      const header = document.querySelector('.header');
      const stockOffers = document.querySelector('#stock-offer');

      // Make sure the page has a header and we can calculate the height...
      if (!header || !stockOffers || typeof header.getBoundingClientRect !== 'function') {
        return;
      }

      // Scroll to the element...
      jump(stockOffers, {
        offset: header.getBoundingClientRect().height * -1,
      });
    },

    onPrint() {
      window.print();
    },

    openCuttingCostsModal() {
      this.cuttingCostsModalOpen = true;
    },

    closeCuttingCostsModal() {
      this.cuttingCostsModalOpen = false;
    },

    gtmAddToCart(formInput) {
      if (typeof window.dataLayer === 'undefined') {
        return;
      }

      window.dataLayer.push({ ecommerce: null });

      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [{
            item_name: formInput.fefco,
            item_id: formInput.fefco,
            price: formInput.pricePerPiece,
            item_brand: 'Dozen laten maken',
            item_variant: formInput.type,
            item_list_name: 'Guide',
            index: 1,
            quantity: formInput.amount,
          }],
        },
      });
    },
  },
};
</script>

<style scoped>
.custom-offers {
  background-color: #fff;
}

.custom-offers__head {
  background-color: #231f20;
  color: #fff;
}

h2.custom-offers__head {
  font-size: 1.25rem;
}

.custom-offers__head h2 {
  font-size: 1.25rem;
}

.custom-offers__cart {
  background-color: #4eab73;
}

.custom-offers__cart:disabled {
  background-color: #e0e0d9;
}

.custom-offers__suggestion {
  border-radius: .25rem;
}

.stock-offers-shortcut {
  padding: .875rem;
  background-color: #f6f1e9;
  border: 1px solid #ebe3d6;
  color: #605544;
  border-radius: .375rem;
}

.stock-offers-shortcut__button {
  border-bottom: 1px solid rgba(0, 0, 0, .3);
}

.stock-offers-shortcut__button :global(svg) {
  fill: currentColor;
  width: .75em;
  height: .75em;
  display: inline-block;
  vertical-align: middle;
  margin-left: .25em;
}

.stock-offers-shortcut__title :global(svg) {
  fill: currentColor;
  width: 1.25em;
  height: 1.25em;
  margin-right: .25em;
  display: inline-block;
  vertical-align: middle;
}

.custom-offers__head-action :global(svg) {
  width: 1em;
  height: 1em;
  fill: currentColor;
  vertical-align: middle;
  margin-right: .25rem;
}

.custom-offers__cutting-costs-btn {
  padding: 0;
  background: transparent;
  border: 0;
  text-decoration: underline;
  cursor: pointer;
}

.custom-offers__cutting-costs-btn:hover {
  color: #439765;
}
</style>
